/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { keyframes } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { cssMap, cx, jsx } from '@atlaskit/css';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import type { CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import {
	Breakpoint,
	useElementBreakpoints,
} from '@atlassian/generative-ai-modal/hooks/useElementBreakpoints';
import { RovoThinkingBarWrapper } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBarWrapper';
import type { RovoThinkingScrollMinimizationOptions } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingMinimization';
import {
	RovoGradientKeyframesStyle,
	ROVO_GRADIENT_BACKGROUND,
} from '@atlassian/rovo-platform-ui-components/rovo-gradient';

import { agentAvatarCache } from '../../common/ui/prompt-agent-avatar/agent-avatar-cache';
import { useInlineChatController } from '../../controllers/inline-chat-controller';
import { AgentHeader } from './agent-header';
import messages from './messages';
import type { PromptContentChangePayload } from './prompt-content-change-payload';
import { ErrorView } from './views/error-view';
import { PromptView } from './views/prompt-view';
import { SuccessView } from './views/success-view';
import { ThinkingView } from './views/thinking-view';
import { WarningView } from './views/warning-view';

const PromptViewWithRelay = React.lazy(
	() =>
		import(
			/* webpackChunkName: "@atlassian/conversation-assistant-chat-prompt-input/prompt-view-with-relay" */ './views/prompt-view-with-relay'
		),
);

const LazyThinkingBarShell = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant-chat-prompt-input/thinking-bar-shell" */ './ThinkingBarShell'
	).then(({ ThinkingBarShell }) => ({ default: ThinkingBarShell })),
);

type ThinkingBarShellWrapperProps = {
	children: JSX.Element;
	/**
	 * When false the children are rendered untouched, so the shell (and its lazy
	 * chunk) is only pulled in for the consumers that opted into it.
	 */
	isEnabled: boolean;
	isThinking: boolean;
	scrollMinimization?: RovoThinkingScrollMinimizationOptions;
	onDisplayedMinimizedChange?: (isMinimized: boolean) => void;
	thinkingBarWrapper?: (thinkingBar: JSX.Element) => JSX.Element;
};

/**
 * Optional scroll-minimization shell around the prompt. Declared at module scope
 * and rendered as JSX so React keeps a stable identity for the lazy component
 * across re-renders, instead of re-suspending and losing its internal state.
 */
const ThinkingBarShellWrapper = ({
	children,
	isEnabled,
	isThinking,
	scrollMinimization,
	onDisplayedMinimizedChange,
	thinkingBarWrapper,
}: ThinkingBarShellWrapperProps): JSX.Element => {
	if (!isEnabled) {
		return children;
	}

	return (
		// The fallback is wrapped too so the bar is positioned from the first frame
		// instead of jumping once the shell chunk resolves.
		<React.Suspense fallback={thinkingBarWrapper ? thinkingBarWrapper(children) : children}>
			<LazyThinkingBarShell
				isThinking={isThinking}
				scrollMinimization={scrollMinimization}
				onDisplayedMinimizedChange={onDisplayedMinimizedChange}
				thinkingBarWrapper={thinkingBarWrapper}
			>
				{children}
			</LazyThinkingBarShell>
		</React.Suspense>
	);
};

const slideFadeIn = keyframes({
	from: { opacity: 0, transform: 'translateY(1rem)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const styles = cssMap({
	gradientWrapper: {
		animationName: 'rovoGradientRotation',
		animationDuration: token('motion.duration.xxlong', '2s'),
		animationTimingFunction: 'linear',
		animationIterationCount: 'infinite',
		borderRadius: token('radius.xxlarge'),
		width: '50%',
		margin: '0 auto',
	},
	gradientInner: {
		paddingBlock: token('space.025'),
		paddingInline: token('space.025'),
		backgroundColor: token('color.background.input'),
		borderRadius: 'inherit',
		backgroundClip: 'content-box',
	},
	wrapper: {
		width: 'fit-content',
		paddingInline: token('space.200'),
		paddingBlock: token('space.150'),
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.input'),
		boxShadow: token('elevation.shadow.overlay'),
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
	},

	fadeIn: {
		animationName: slideFadeIn,
		animationDuration: token('motion.duration.medium', '0.2s'),
		animationTimingFunction: token('motion.easing.out.practical', 'ease-out'),
		animationFillMode: 'forwards',
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
			transition: 'none',
		},
	},

	wrapperFlex: {
		width: '100%',
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'space-between',
		gap: token('space.100'),
	},

	wrapperFlexCenterAligned: {
		alignItems: 'center',
	},

	wrapperFlexTopAligned: {
		alignItems: 'flex-start',
	},

	wrapperFlexNoWrap: {
		flexWrap: 'nowrap',
	},

	agentHeaderSpacer: {
		// @ts-expect-error - cssMap height/width does not accept pixel or calc() strings outside the defined union
		marginTop: '46px', // eslint-disable-line @atlaskit/design-system/use-tokens-space
		paddingBottom: token('space.025'),
	},

	compactStyle: {
		paddingInline: token('space.150'),
		paddingBlock: token('space.100'),
		// @ts-expect-error - cssMap height/width does not accept pixel or calc() strings outside the defined union
		width: `calc(100% - ${token('space.300')})`, // eslint-disable-line @atlaskit/design-system/use-tokens-space
		margin: '0 auto',
	},

	fullWidth: {
		width: '100%',
	},

	editingWidth: {
		width: '100%',
		maxWidth: '400px',
		margin: '0 auto',
	},

	thinking: {
		width: '50%',
		margin: '0 auto',
	},

	// Outer positioning layer when the shared RovoThinkingBarWrapper supplies the
	// gradient border. No animation/background here — the wrapper provides those.
	// Width matches the legacy `gradientWrapper` (50%, centered) so the unified
	// thinking bar keeps the same width as before.
	unifiedOuter: {
		width: '50%',
		margin: '0 auto',
		borderRadius: token('radius.xxlarge'),
	},

	thinkingIsCancelling: {
		margin: '0 auto',
	},

	thinkingUpdatedPadding: {
		paddingBlock: token('space.100'),
	},

	centering: {
		margin: '0 auto',
	},

	// `frameless` appearance — used when the RovoInlineChatPrompt is embedded
	// inside another surface that already provides its own padding, background
	// border-radius and elevation (e.g. the Post Stream Review toolbar). The
	// outer dialog Box becomes a pure transparent passthrough so it blends in.
	frameless: {
		paddingInline: token('space.0'),
		paddingBlock: token('space.0'),
		backgroundColor: 'transparent',
		// `initial` removes the elevation.shadow.overlay set on `wrapper` without
		// using a token (cssMap's typed contract for boxShadow only accepts the
		// allowlisted shadow tokens plus `initial`).
		boxShadow: 'initial',
		// 0 border-radius — there is no `radius.0` token in the design system.
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderRadius: 0,
	},
});
type RovoInlineChatPromptProps = {
	source: string;
	productKey: string;
	siteId: string;
	orgId?: string;
	objectId?: string | number;
	onAbort: ({ streamEnded }: { streamEnded: boolean }) => void;
	/**
	 * Called when an inline stream error should hand off to side chat (openChatOnError).
	 * Falls back to calling onAbort when onError is not passed.
	 */
	onError?: ({ streamEnded }: { streamEnded: boolean }) => void;
	onComplete: () => void;
	/**
	 * `cancel` is the escape-key cancel (may show the cancel warning). `abortTurn` ends the turn
	 * immediately and tells Rovo Chat to stop it, even while the prompt is hidden behind Rovo Chat.
	 */
	onSubmit?: (cancel: () => void, abortTurn: () => void) => void;
	onFollow?: () => void;
	compact?: boolean;
	onCancel?: () => void;
	autoClose?: boolean;
	enableAgents?: boolean;
	/**
	 * The account ID of the current user. Required when `enableAgents` is true
	 * to power the @agent mention typeahead.
	 */
	currentUserId?: string;
	willDeleteAfterAbort?: boolean;
	onBeforeComplete?: (complete: () => void) => void;
	/**
	 * Keeps the prompt/controller mounted after completion but renders no prompt UI.
	 * Useful for host surfaces that replace the prompt with their own completion UI.
	 */
	hideAfterComplete?: boolean;
	showMauiMediaPlaceholder?: boolean;
	/**
	 * When true, the side chat will open if Rovo encounters an error during the inline edit.
	 */
	openChatOnError?: boolean;
	/**
	 * Optional creation context parameters passed through to the backend for
	 * experience-based agent routing. Consumers should set this to provide the
	 * appropriate experience and content types for their surface (e.g. page,
	 * whiteboard, database).
	 */
	creationContextParams?: CreationContextParams;
	/**
	 * Temporary prop to show beta lozenge - for use with editor inline only
	 * @private
	 * @deprecated
	 */
	_showLabsLozenge?: boolean;
	_showLabsLozengeVariant?: 'default' | 'maui';
	/**
	 * Used to control where popups are mounted to (ie. the editor typeahead)
	 */
	popupsMountPoint?: HTMLElement | null;
	/**
	 * Called when the inline prompt editor content changes (text length, empty state, mentions/cards).
	 */
	onPromptContentChange?: (payload: PromptContentChangePayload) => void;
	/**
	 * When provided, called with the prompt DocNode before the inline edit is sent to the
	 * Rovo bridge. If it returns `true`, the bridge send is skipped and the prompt is closed.
	 * Used by the SmartLink Rovo button to open the Rovo sidebar chat instead of an AI edit.
	 */
	onBeforeSubmit?: (prompt: DocNode) => boolean;
	/** Called only when an already-open Rovo Chat takes over immediately after bridge send. */
	onSubmitAutoClose?: () => void;
	/** Returns the editor-owned token used to correlate terminal stream events. */
	getInlineRovoEditAttemptId?: () => string | undefined;
	/**
	 * Optional initial ADF document to pre-fill the prompt editor with.
	 * Used by the editor slash menu to pre-fill with a selected skill chip.
	 */
	defaultValue?: JSONDocNode;
	/**
	 * Controls the outer-surface chrome of the inline chat prompt.
	 *
	 * - `default` renders the standard padded, rounded, elevated dialog surface.
	 * - `frameless` removes that chrome and hides trailing actions so an embedding
	 *   surface can provide its own framing and controls.
	 *
	 * Pair `frameless` with `showTrailingActions` to re-show the add and submit controls.
	 */
	appearance?: 'default' | 'frameless';
	/**
	 * Shows the prompt's add and submit controls while retaining the frameless surface.
	 * It has no effect with the default appearance, where those controls are always present.
	 */
	showTrailingActions?: boolean;
	/** Keeps inline prompt controls on the top row as the editor grows, without wrapping trailing actions. */
	keepTrailingActionsInline?: boolean;
	/** Shows the agent selector in frameless mode. */
	showAgentSelectorWhenFrameless?: boolean;
	/** Hides the inline agent name while preserving its selector affordance. */
	hideAgentName?: boolean;
	/**
	 * Controls attachment placement:
	 * - `undefined`: render attachments inline, the default presentation.
	 * - `null`: a full-width rail is requested but its mount point is not ready.
	 * - `HTMLElement`: render attachments into that full-width rail.
	 */
	attachmentListMountPoint?: HTMLElement | null;
	shouldRenderInPortal?: boolean;
	/**
	 * Overrides the package-default placeholder. Consumers control when this is supplied
	 * so other prompt surfaces retain their own default copy.
	 */
	placeholderText?: string;
	/** Where to render agent selection when agents are enabled. */
	agentSelectorLayout?: 'menu' | 'inline';
	/**
	 * Enables scroll-triggered minimization of the existing thinking bar. The
	 * caller supplies its scroll container and controls rollout, so the default
	 * prompt behavior remains unchanged.
	 */
	thinkingBarScrollContainer?: HTMLElement | Window;
	/**
	 * Optionally wraps the thinking shell after the prompt is submitted. The
	 * caller controls when to supply it, leaving the initial prompt in place.
	 */
	thinkingBarWrapper?: (thinkingBar: React.JSX.Element) => React.JSX.Element;
	/** Matches the shared Editing loading-bar width for the thinking bar and its outer chrome. */
	useEditingWidthThinkingBar?: boolean;
	/** Receives imperative prompt controls. */
	controlsRef?: React.Ref<{ clear: () => void }>;
	onThinkingBarDisplayedMinimizedChange?: (isMinimized: boolean) => void;
};

export const RovoInlineChatPrompt = (props: RovoInlineChatPromptProps): JSX.Element | null => {
	di(useInlineChatController);

	// When showMauiMediaPlaceholder is true, defer the completion transition so
	// the progress bar can fill to 100% before the thinking view unmounts.
	const [isProgressBarComplete, setIsProgressBarComplete] = useState(false);
	const pendingCompleteRef = useRef<(() => void) | null>(null);

	const { showMauiMediaPlaceholder, onBeforeComplete: propsOnBeforeComplete } = props;
	// Hidden when the media-node overlay is in use.
	const isProgressBarEnabled =
		!!showMauiMediaPlaceholder &&
		!(fg('cc-maui-phase-2') && expValEquals('cc-maui-experiment', 'isEnabled', true));

	const onBeforeComplete = useCallback(
		(complete: () => void) => {
			if (!isProgressBarEnabled) {
				if (propsOnBeforeComplete) {
					propsOnBeforeComplete(complete);
				} else {
					complete();
				}
				return;
			}
			// Defer until the progress bar's fill-to-100% animation finishes.
			const finalComplete = propsOnBeforeComplete
				? () => propsOnBeforeComplete(complete)
				: complete;
			pendingCompleteRef.current = finalComplete;
			setIsProgressBarComplete(true);
		},
		[isProgressBarEnabled, propsOnBeforeComplete],
	);

	const onCompleteAnimationFinished = useCallback(() => {
		pendingCompleteRef.current?.();
		pendingCompleteRef.current = null;
		setIsProgressBarComplete(false);
	}, []);

	/**
	 * Resolved once here and passed down, so `PromptView` doesn't have to repeat
	 * the same condition.
	 *
	 * `frameless` consumers are excluded unless they opt in. That appearance
	 * already hides the trailing action cluster, but this selector lives inside
	 * the editor row, so without the check it would appear for every frameless
	 * consumer rather than only those designed to surface it.
	 */
	const showInlineAgentSelector =
		props.agentSelectorLayout === 'inline' &&
		!!props.enableAgents &&
		(props.appearance !== 'frameless' || !!props.showAgentSelectorWhenFrameless);

	const {
		mode,
		isCancelling,
		latestTrace,
		error,
		submit,
		resubmit,
		cancel,
		keep,
		abort,
		autoClose,
		agent,
		setAgent,
	} = useInlineChatController({
		...props,
		onBeforeComplete,
		routeDefaultRovoThroughChat: showInlineAgentSelector,
	});
	const dialogRef = useRef<HTMLDivElement | null>(null);
	const thinkingBarScrollMinimization = React.useMemo(
		() => ({
			scrollContainer: props.thinkingBarScrollContainer,
			isEnabled: !isCancelling,
		}),
		[isCancelling, props.thinkingBarScrollContainer],
	);
	const [isBrowseAgentsModalOpen, setIsBrowseAgentsModalOpen] = useState(false);

	// The selector already shows the selected agent inline, so the header bar
	// above the input would be a duplicate.
	const showAgentHeader = !showInlineAgentSelector;
	const shouldCompensateEmptyPromptMargin =
		props.productKey === 'jira' && fg('jira_rovo_prompt_box_margin');
	const [setBreakpointElement, { isSmall }] = useElementBreakpoints(Breakpoint.SmallChromeless);
	const { formatMessage } = useIntl();

	const useShortMessages = props.compact && isSmall;

	const setDialogRef = useCallback(
		(element: HTMLDivElement | null) => {
			dialogRef.current = element;
			setBreakpointElement(element);
		},
		[setBreakpointElement],
	);

	useEffect(() => {
		const onMouseDown = (e: MouseEvent) => {
			// Don't auto-close during thinking mode — the user should use the stop
			// button or Escape key to cancel while a request is in progress.
			// Don't auto-close during error mode — clicking outside the error box
			// should not trigger the warning view.
			if (
				mode === 'thinking' ||
				// While hidden for Rovo Chat, ignore outside clicks too — we don't
				// want a click to dispatch cancel and put us into the warning view
				// when chat closes.
				mode === 'hidden' ||
				mode === 'complete-hidden' ||
				(isExperimentEnabled('platform_editor_ai_fix_error_discard_screen') && mode === 'error')
			) {
				return;
			}

			const isClickInsideDialog = dialogRef.current?.contains(e.target as Node);
			const isClickInsidePopupMount = props.popupsMountPoint?.contains(e.target as Node);
			// The agent picker portals out of the prompt so it can sit on top of
			// editor / product chrome. Treat those clicks as inside so selecting
			// an agent does not dismiss us.
			const isClickInsideChatHeaderMenu =
				e.target instanceof Element && !!e.target.closest('[data-chat-header-menu]');

			if (
				!isClickInsideDialog &&
				!isClickInsidePopupMount &&
				!isClickInsideChatHeaderMenu &&
				!isBrowseAgentsModalOpen
			) {
				if (autoClose) {
					cancel();
				}
			}
		};

		// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage -- pre-existing, keyboard event listener
		return bind(document, {
			type: 'mousedown',
			listener: onMouseDown,
		});
	}, [mode, cancel, autoClose, isBrowseAgentsModalOpen, props.popupsMountPoint]);

	useEffect(() => {
		return () => {
			agentAvatarCache.clear();
		};
	}, []);

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Escape') {
			cancel({ skipWarning: true });
		}
	};

	const willDeleteAfterAbort = props.willDeleteAfterAbort ?? true;
	const renderView = () => {
		if (isCancelling) {
			return (
				<WarningView
					onKeep={keep}
					onDelete={() => abort(true)}
					willDeleteAfterAbort={willDeleteAfterAbort}
				/>
			);
		}

		switch (mode) {
			case 'thinking':
				return (
					<ThinkingView
						latestTrace={latestTrace}
						onCancel={cancel}
						onFollow={props.onFollow}
						useShortMessages={useShortMessages}
						showProgressBar={isProgressBarEnabled}
						isComplete={isProgressBarComplete}
						onCompleteAnimationFinished={onCompleteAnimationFinished}
					/>
				);
			case 'editing': {
				const promptViewProps = {
					...props,
					showLabsLozenge: props._showLabsLozenge,
					labsLozengeVariant: props._showLabsLozengeVariant,
					showMauiMediaPlaceholder: props.showMauiMediaPlaceholder,
					onSubmit: submit,
					onAgentSelect: setAgent,
					isBrowseAgentsModalOpen,
					setIsBrowseAgentsModalOpen,
					agent,
					useShortMessages,
					appearance: props.appearance,
					placeholderText: props.placeholderText,
					attachmentListMountPoint: props.attachmentListMountPoint,
					showInlineAgentSelector,
					shouldCompensateEmptyPromptMargin,
				};
				return (expValEquals('platform_editor_agent_mentions', 'isEnabled', true) &&
					fg('platform_editor_agent_mentions_drop_one_fixes')) ||
					fg('platform_editor_agent_mention_ask_rovo_jira') ? (
					<React.Suspense fallback={<PromptView {...promptViewProps} />}>
						<PromptViewWithRelay {...promptViewProps} />
					</React.Suspense>
				) : (
					<PromptView {...promptViewProps} />
				);
			}
			case 'error':
				return <ErrorView error={error} onClose={abort} onTryAgain={resubmit} />;
			case 'success':
				return <SuccessView />;
			default:
				return null;
		}
	};

	// When hidden for Rovo Chat or after a host-owned completion, render nothing
	// while keeping the component and its reducer/bridge state mounted.
	if (mode === 'hidden' || mode === 'complete-hidden') {
		return null;
	}

	// The bar is wrapped in the animated gradient border when thinking.
	const isInGradientBorder =
		mode === 'thinking' &&
		!isCancelling &&
		!isProgressBarEnabled &&
		expValEquals('cc-maui-experiment', 'isEnabled', true);

	// Keep the wrapper mounted across `thinking` -> `editing` so only the inner view
	// swaps. Remounting it caused a brief flash at the transition.
	const useStableWrapper =
		!isCancelling &&
		!isProgressBarEnabled &&
		(mode === 'thinking' || mode === 'editing') &&
		fg('platform_editor_ai_stable_thinking_bar_wrapper') &&
		expValEquals('cc-maui-experiment', 'isEnabled', true);

	// When unified, RovoThinkingBarWrapper supplies the chrome, so the dialog Box
	// drops its own to avoid doubling. Stable-wrapper also routes through here.
	const useUnifiedWrapper = isInGradientBorder || useStableWrapper;

	const bar = (
		<Box
			ref={setDialogRef}
			role="dialog"
			aria-label={formatMessage(messages.dialogAriaLabel)}
			onKeyDown={onKeyDown}
			xcss={cx(
				styles.wrapper,
				mode === 'editing' && styles.fullWidth,
				mode === 'thinking' &&
					!isCancelling &&
					props.useEditingWidthThinkingBar &&
					styles.editingWidth,
				mode === 'thinking' &&
					!isCancelling &&
					!props.useEditingWidthThinkingBar &&
					!isInGradientBorder &&
					styles.thinking,
				mode === 'thinking' && styles.thinkingIsCancelling,
				mode === 'thinking' && styles.fadeIn,
				mode === 'thinking' && styles.thinkingUpdatedPadding,
				props.compact && styles.compactStyle,
				// Fill the gradient border instead of compactStyle's narrower width.
				isInGradientBorder && styles.fullWidth,
				styles.centering,
				// `frameless` strips the dialog chrome so the prompt blends into
				// a parent container that already provides its own framing.
				// Applied last so it wins over wrapper / compact / centering.
				// Also stripped only while the unified RovoThinkingBarWrapper supplies the chrome.
				(props.appearance === 'frameless' || (useUnifiedWrapper && mode === 'thinking')) &&
					styles.frameless,
			)}
		>
			{showAgentHeader && agent && (
				<AgentHeader agent={agent} removeAgent={() => setAgent(null)} compact={props.compact} />
			)}
			<Box
				xcss={cx(
					styles.wrapperFlex,
					props.keepTrailingActionsInline
						? styles.wrapperFlexTopAligned
						: styles.wrapperFlexCenterAligned,
					props.keepTrailingActionsInline && styles.wrapperFlexNoWrap,
					showAgentHeader && agent && styles.agentHeaderSpacer,
				)}
			>
				{renderView()}
			</Box>
		</Box>
	);

	const isThinkingBarShellEnabled =
		!!props.thinkingBarScrollContainer && (mode === 'thinking' || mode === 'editing');

	const thinkingBarShellProps = {
		isEnabled: isThinkingBarShellEnabled,
		isThinking: mode === 'thinking',
		scrollMinimization: thinkingBarScrollMinimization,
		onDisplayedMinimizedChange: props.onThinkingBarDisplayedMinimizedChange,
		// Only the thinking bar is handed to the host wrapper. `editing` renders the
		// prompt input, which stays inline where the user invoked it.
		thinkingBarWrapper: mode === 'thinking' ? props.thinkingBarWrapper : undefined,
	};

	if (useUnifiedWrapper) {
		// RovoThinkingBarWrapper supplies the chrome; the outer div only centers and
		// applies the AI modal's 2rem bleed (ModalRegion.tsx) to align with the edges.
		return (
			<ThinkingBarShellWrapper {...thinkingBarShellProps}>
				<div
					css={[styles.unifiedOuter, props.useEditingWidthThinkingBar && styles.editingWidth]}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- calc() width can't be expressed in cssMap
					style={props.compact ? { width: 'calc(100% - 2rem)' } : undefined}
					data-testid="thinking-bar-gradient-border"
				>
					<RovoThinkingBarWrapper isThinking={mode === 'thinking'}>{bar}</RovoThinkingBarWrapper>
				</div>
			</ThinkingBarShellWrapper>
		);
	}

	if (isInGradientBorder) {
		// Subtract the AI modal's 2rem bleed (ModalRegion.tsx) so the border lines
		// up with the editor edges instead of overflowing.
		const gradientBorderStyle = {
			background: ROVO_GRADIENT_BACKGROUND,
			...(props.compact && { width: 'calc(100% - 2rem)' }),
		};

		return (
			<ThinkingBarShellWrapper {...thinkingBarShellProps}>
				<div
					css={[styles.gradientWrapper, props.useEditingWidthThinkingBar && styles.editingWidth]}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- gradient background and calc() width can't be expressed in cssMap
					style={gradientBorderStyle}
					data-testid="thinking-bar-gradient-border"
				>
					<RovoGradientKeyframesStyle />
					<div css={styles.gradientInner}>{bar}</div>
				</div>
			</ThinkingBarShellWrapper>
		);
	}

	if (
		fg('rovo_agents_studio_browse_kit_chat') &&
		expVal('jira_browse_agent_modal_update', 'isEnabled', false)
	) {
		return (
			<ThinkingBarShellWrapper {...thinkingBarShellProps}>
				<ModalContextProvider>{bar}</ModalContextProvider>
			</ThinkingBarShellWrapper>
		);
	}

	return <ThinkingBarShellWrapper {...thinkingBarShellProps}>{bar}</ThinkingBarShellWrapper>;
};
